/**
 * Welche Datei bekommt der Kunde, wenn er auf eine Rechnung klickt?
 *
 * Bis zur Umstellung zeigte der Knopf auf `invoice_pdf` - Stripes eigene
 * Adresse. Das ist kein Beleg zum Aufbewahren, es ist eine fremde Adresse, und
 * es ist keine E-Rechnung. Seitdem legen wir den Beleg selbst ab und geben eine
 * kurzlebige Adresse darauf heraus.
 *
 * Warum die Entscheidung hier steht und nicht im Klick-Behandler: Im Behandler
 * haengt sie an Apollo, Redux, i18n und der Bauteil-Bibliothek und waere damit
 * praktisch unpruefbar. Hier liegt sie mit eingespeisten Abhaengigkeiten und
 * laesst sich ohne DOM pruefen.
 *
 * Dieselbe Regel steht in `products/tax2/app_frontend`
 * (`src/lib/billing/invoiceDownload.ts`) und in `app_components`
 * (`src/components/Billing/invoiceDownload.ts`). Drei Fassungen, weil die
 * Oberflaechen keine gemeinsame Bibliothek teilen; wer eine aendert, sollte die
 * anderen ansehen.
 */

/** Was der Aufrufer ueber eine Rechnungszeile weiss. */
export interface InvoiceRef {
  /** Rechnungsnummer, wie sie auf dem Beleg steht. */
  number: string;
  /** Stripes eigene PDF-Adresse, sofern der Dienst eine liefert. */
  invoice_pdf: string;
}

/** Das Ergebnis der Entscheidung. */
export type InvoiceHref =
  | { kind: "document"; url: string; filename: string }
  | { kind: "stripe"; url: string; filename: string }
  | { kind: "none" };

/**
 * Entscheidet, welche Adresse geoeffnet wird.
 *
 * Die Reihenfolge ist die ganze Regel:
 *
 *   1. Unser abgelegter Beleg, wenn es einen gibt.
 *   2. Sonst Stripes PDF. Das ist KEIN Fehlerfall - fuer jede Rechnung vor der
 *      Umstellung gibt es keinen eigenen Beleg, und das ist am Tag der
 *      Umstellung der haeufigste Fall.
 *   3. Gibt es beides nicht, wird nichts geoeffnet. Ein Knopf, der ins Leere
 *      fuehrt, ist schlimmer als einer, der nichts tut.
 *
 * Scheitert der Abruf des Belegs - Netz weg, Dienst down -, wird auf Stripes
 * PDF zurueckgefallen statt abzubrechen: Der Kunde will seine Rechnung, und ein
 * erreichbares Stripe-PDF ist besser als eine Fehlermeldung.
 *
 * @param row Die angeklickte Rechnungszeile.
 * @param fetchDocumentUrl Holt die Adresse unseres Belegs, oder null.
 * @returns Was geoeffnet werden soll, samt Dateiname.
 */
export const resolveInvoiceHref = async (
  row: InvoiceRef,
  fetchDocumentUrl: (invoiceNumber: string) => Promise<string | null>
): Promise<InvoiceHref> => {
  // Der Dateiname war fest "report" - ein Ueberbleibsel aus dem
  // Berichts-Download. Bei fremder Herkunft ist das Attribut ohnehin
  // wirkungslos, aber bei unserem eigenen Beleg greift es, und dann soll dort
  // die Rechnungsnummer stehen.
  const filename = row.number ? `${row.number}.pdf` : "rechnung.pdf";

  if (row.number) {
    try {
      const documentUrl = await fetchDocumentUrl(row.number);
      if (documentUrl) return { kind: "document", url: documentUrl, filename };
    } catch {
      // Rueckfall auf Stripe: Der Kunde will seine Rechnung. Der Fehler bleibt
      // sichtbar, weil der Apollo-Client ihn meldet.
    }
  }

  if (row.invoice_pdf) {
    return { kind: "stripe", url: row.invoice_pdf, filename };
  }

  return { kind: "none" };
};
